import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, Grid, TextField, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useSnackbar } from 'notistack';
import { ConfigContext } from 'components/ConfigGuard';
import {
  getMaxLengthValidationRule,
  getRequiredValidationRule,
  FormProps,
  getAddressControlName,
  countryFieldName,
} from 'util/Form';
import { defaultGridItemProps } from 'util/Layout';
import CountryFormItem from './CountryFormItem';
import { toAlpha3, countryName, DOMESTIC_CODES } from 'util/CountryCodes';

import externalStyles from 'components/profile/EditProfilePageView/EditProfileForm/EditProfileForm.module.scss';

interface SmartySuggestion {
  street_line: string;
  city: string;
  state: string;
  zipcode: string;
}

interface SmartyInternationalCandidate {
  street?: string;
  locality?: string;
  administrative_area?: string;
  postal_code?: string;
  address_text?: string;
  address_id?: string;
}

interface AddressFormSectionProps extends FormProps {
  addressFieldName?: string;
}


/**
 * Formats a Smarty suggestion into a single display string for the street1 input and dropdown options.
 * US addresses use comma separators (canonical US convention).
 * International addresses use space separators to match Smarty's native address_text format and each
 * country's conventions — avoids imposing US-style comma formatting on international data.
 */
const formatSuggestion = (s: SmartySuggestion | SmartyInternationalCandidate, isUs: boolean): string => {
  if (isUs) {
    const us = s as SmartySuggestion;
    return `${us.street_line}, ${us.city}, ${us.state} ${us.zipcode}`;
  }
  const intl = s as SmartyInternationalCandidate;
  if (!intl.street && intl.address_text) return intl.address_text;
  return [intl.street, intl.locality, intl.administrative_area, intl.postal_code].filter(Boolean).join(' ');
};

const AddressFormSection: React.FunctionComponent<AddressFormSectionProps> = (props) => {
  const {
    inputProps,
    rowProps = defaultGridItemProps,
    required = false,
    disabled = false,
    addressFieldName = 'address',
  } = props;

  const { control, setValue } = useFormContext();
  const { smartyEmbeddedKey } = useContext(ConfigContext);
  const { enqueueSnackbar } = useSnackbar();

  const getControlName = useCallback(
    (label: string) => getAddressControlName(addressFieldName, label),
    [addressFieldName]
  );

  const countryValue: string = useWatch({ control, name: getControlName(countryFieldName) });
  const cityValue: string = useWatch({ control, name: getControlName('city') });
  const stateValue: string = useWatch({ control, name: getControlName('stateRegion') });
  const zipValue: string = useWatch({ control, name: getControlName('postalCode') });

  const [region, setRegion] = useState<'us' | 'international'>('us');
  const [regionInitialized, setRegionInitialized] = useState(false);
  const [domesticSuggestions, setDomesticSuggestions] = useState<SmartySuggestion[]>([]);
  const [intlCandidates, setIntlCandidates] = useState<SmartyInternationalCandidate[]>([]);
  const [selectedSuggestion, setSelectedSuggestion] = useState<SmartySuggestion | SmartyInternationalCandidate | null>(null);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortController = useRef<AbortController | null>(null); // cancels in-flight suggestion fetches (debounced while typing)
  const detailsAbortController = useRef<AbortController | null>(null); // cancels in-flight drill-down so a stale response doesn't overwrite a newer selection
  const hasModified = useRef(false);
  const selectedSuggestionRef = useRef<SmartySuggestion | SmartyInternationalCandidate | null>(null);

  // Initialize region from existing address once form data loads
  useEffect(() => {
    if (!regionInitialized && countryValue) {
      setRegion(DOMESTIC_CODES.has(countryValue) ? 'us' : 'international');
      setRegionInitialized(true);
    }
  }, [countryValue, regionInitialized]);

  // Clear stale suggestions and address fields when country changes
  useEffect(() => {
    setIntlCandidates([]);
    if (regionInitialized && countryValue) {
      setSelectedSuggestion(null);
      selectedSuggestionRef.current = null;
      hasModified.current = false;
      setValue(getControlName('street1'), '');
      setValue(getControlName('city'), '');
      setValue(getControlName('stateRegion'), '');
      setValue(getControlName('postalCode'), '');
    }
  }, [countryValue]);

  // Cleanup on unmount — cancels any pending debounce or in-flight fetch so they don't
  // attempt to update state after the component is gone, preventing a memory leak.
  useEffect(() => {
    return () => {
      if (debounceTimer.current) clearTimeout(debounceTimer.current);
      if (abortController.current) abortController.current.abort();
      if (detailsAbortController.current) detailsAbortController.current.abort();
    };
  }, []);

  const fetchDomesticSuggestions = useCallback((query: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    if (query.length < 3 || !smartyEmbeddedKey) { setDomesticSuggestions([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://us-autocomplete-pro.api.smarty.com/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&max_results=5`,
          { signal: abortController.current.signal }
        );
        if (!res.ok) throw new Error(`Smarty US error: ${res.status}`);
        const data = await res.json();
        setDomesticSuggestions(data.suggestions ?? []);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setDomesticSuggestions([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const fetchInternationalSuggestions = useCallback((query: string, countryCode: string) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    const alpha3 = toAlpha3(countryCode);
    if (query.length < 3 || !smartyEmbeddedKey || !alpha3) { setIntlCandidates([]); return; }
    debounceTimer.current = setTimeout(async () => {
      if (abortController.current) abortController.current.abort();
      abortController.current = new AbortController();
      try {
        const res = await fetch(
          `https://international-autocomplete.api.smarty.com/v2/lookup?key=${smartyEmbeddedKey}&search=${encodeURIComponent(query)}&country=${alpha3}&max_results=5`,
          { signal: abortController.current.signal }
        );
        if (!res.ok) throw new Error(`Smarty International error: ${res.status}`);
        const data = await res.json();
        setIntlCandidates(data.candidates ?? []);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setIntlCandidates([]);
        enqueueSnackbar('Unable to load address suggestions. Please try again.', { variant: 'error' });
      }
    }, 500);
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  // Second Smarty call — drill-down on a selected address_id to get decomposed fields
  // (street, locality, administrative_area, postal_code). Smarty's autocomplete only
  // returns these structured fields through this follow-up endpoint.
  const fetchInternationalDetails = useCallback(async (addressId: string, countryCode: string): Promise<SmartyInternationalCandidate | null> => {
    const alpha3 = toAlpha3(countryCode);
    if (!smartyEmbeddedKey || !alpha3) return null;
    if (detailsAbortController.current) detailsAbortController.current.abort();
    detailsAbortController.current = new AbortController();
    try {
      const res = await fetch(
        `https://international-autocomplete.api.smarty.com/v2/lookup/${encodeURIComponent(addressId)}?key=${smartyEmbeddedKey}&country=${alpha3}`,
        { signal: detailsAbortController.current.signal }
      );
      if (!res.ok) throw new Error(`Smarty International detail error: ${res.status}`);
      const data = await res.json();
      return data.candidates?.[0] ?? null;
    } catch (err: any) {
      if (err?.name === 'AbortError') return null;
      enqueueSnackbar('Unable to load address details. Please try again.', { variant: 'error' });
      return null;
    }
  }, [enqueueSnackbar, smartyEmbeddedKey]);

  const handleRegionChange = useCallback((_: any, newRegion: 'us' | 'international' | null) => {
    if (!newRegion) return;
    setRegion(newRegion);
    setDomesticSuggestions([]);
    setIntlCandidates([]);
    setSelectedSuggestion(null);
    selectedSuggestionRef.current = null;
    hasModified.current = false;
    setValue(getControlName('street1'), '');
    setValue(getControlName('street2'), '');
    setValue(getControlName('city'), '');
    setValue(getControlName('stateRegion'), '');
    setValue(getControlName('postalCode'), '');
    if (newRegion === 'us') {
      setValue(getControlName('countryCode'), 'US');
      setValue(getControlName('country'), countryName('US') ?? '');
    } else {
      setValue(getControlName('countryCode'), '');
      setValue(getControlName('country'), '');
    }
  }, [getControlName, setValue]);

  return (
    <Grid container>
      {/* Region Toggle */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Region
        </Typography>
        <ToggleButtonGroup
          value={region}
          exclusive
          onChange={handleRegionChange}
          disabled={disabled}
          sx={{ mt: '16px', mb: '24px' }}
        >
          <ToggleButton value="us">United States</ToggleButton>
          <ToggleButton value="international">International</ToggleButton>
        </ToggleButtonGroup>
      </Grid>

      {/* International: Country Selector */}
      {region === 'international' && (
        <Grid {...rowProps} xs={12}>
          <Typography variant={'h5'} className={externalStyles.fieldName}>
            Country
          </Typography>
          <CountryFormItem {...props} addressFieldName={addressFieldName} />
        </Grid>
      )}

      {/* Address Autocomplete */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Address
        </Typography>
        <Controller
          name={getControlName('street1')}
          control={control}
          rules={{
            required: getRequiredValidationRule('address', false, required),
            maxLength: getMaxLengthValidationRule(150),
            validate: () => !hasModified.current || !!selectedSuggestionRef.current || 'Please select an address from the suggestions',
          }}
          render={({ field, fieldState }) => (
            <Autocomplete
              freeSolo
              filterOptions={(x) => x}
              options={(region === 'us' ? domesticSuggestions : intlCandidates) as (SmartySuggestion | SmartyInternationalCandidate)[]}
              getOptionLabel={(o) => typeof o === 'string' ? o : formatSuggestion(o, region === 'us')}
              // Controls what displays in the input field. If the user has selected a suggestion, show the formatted version of it.
              // Otherwise (initial page load with existing SF data), rebuild the display from the individual form fields.
              // Uses commas for US (canonical convention) and spaces for international (matches Smarty's native format).
              // .filter(Boolean) skips empty fields to avoid dangling separators when a user has partial pre-Smarty manual data.
              inputValue={selectedSuggestion
                ? formatSuggestion(selectedSuggestion, region === 'us')
                : region === 'us'
                  ? [field.value, cityValue, [stateValue, zipValue].filter(Boolean).join(' ')].filter(Boolean).join(', ')
                  : [field.value, cityValue, stateValue, zipValue].filter(Boolean).join(' ')}
              onInputChange={(_, value, reason) => {
                if (reason === 'reset') return;
                selectedSuggestionRef.current = null;
                setSelectedSuggestion(null);
                if (!value) {
                  hasModified.current = false;
                } else if (reason === 'input') {
                  hasModified.current = true;
                }
                field.onChange(value);
                setValue(getControlName('city'), '');
                setValue(getControlName('stateRegion'), '');
                setValue(getControlName('postalCode'), '');
                if (value && reason === 'input') {
                  region === 'us'
                    ? fetchDomesticSuggestions(value)
                    : fetchInternationalSuggestions(value, countryValue);
                }
              }}
              onChange={async (_, value) => {
                if (value && typeof value !== 'string') {
                  if (region === 'us') {
                    const s = value as unknown as SmartySuggestion;
                    selectedSuggestionRef.current = s;
                    field.onChange(s.street_line);
                    setSelectedSuggestion(s);
                    setValue(getControlName('city'), s.city);
                    setValue(getControlName('stateRegion'), s.state);
                    setValue(getControlName('postalCode'), s.zipcode);
                    setValue(getControlName('countryCode'), 'US');
                    setValue(getControlName('country'), countryName('US') ?? '');
                  } else {
                    const c = value as unknown as SmartyInternationalCandidate;
                    // Drill down on the selected candidate to get decomposed fields (street, locality,
                    // administrative_area, postal_code). Smarty's international autocomplete only
                    // returns these through this follow-up call. Fall back to address_text if it fails.
                    setIsFetchingDetails(true);
                    const detail = c.address_id ? await fetchInternationalDetails(c.address_id, countryValue) : null;
                    setIsFetchingDetails(false);
                    const resolved = detail ?? c;
                    selectedSuggestionRef.current = resolved;
                    setSelectedSuggestion(resolved);
                    // SF's MailingStateCode is a restricted picklist that rejects international division names.
                    // Combine street + administrative_area into MailingStreet so the division data is preserved
                    // without triggering SF's picklist validation, and leave stateRegion empty.
                    const combinedStreet = [resolved.street, resolved.administrative_area].filter(Boolean).join(' ') || c.address_text || '';
                    field.onChange(combinedStreet);
                    setValue(getControlName('city'), resolved.locality ?? '');
                    setValue(getControlName('stateRegion'), '');
                    setValue(getControlName('postalCode'), resolved.postal_code ?? '');
                    // countryCode is set by CountryFormItem. country (e.g. 'Germany') is only set when a Smarty suggestion is selected.
                    // This is intentional as SF uses countryCode to identify the country, not this field.
                    setValue(getControlName('country'), countryName(countryValue) ?? '');
                  }
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...inputProps}
                  {...params}
                  fullWidth
                  disabled={disabled || isFetchingDetails}
                  placeholder="Start typing your address..."
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  className={externalStyles.inputField}
                  inputProps={{ ...params.inputProps, 'data-testid': 'street1-input' }}
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {isFetchingDetails ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  data-testid="street1-text-field"
                />
              )}
            />
          )}
        />
      </Grid>

      {/* Apt/Unit */}
      <Grid {...rowProps} xs={12}>
        <Typography variant={'h5'} className={externalStyles.fieldName}>
          Apt, Unit, Suite, etc (Optional)
        </Typography>
        <Controller
          render={({ field }) => (
            <TextField
              {...field}
              {...inputProps}
              disabled={disabled}
              inputProps={{ 'data-testid': 'street2-input' }}
              data-testid={'street2-text-field'}
              className={externalStyles.inputField}
              FormHelperTextProps={{ 'data-testid': 'street2-helper-text' } as any}
            />
          )}
          name={getControlName('street2')}
          control={control}
          rules={{ required: getRequiredValidationRule('address', false, required) }}
        />
      </Grid>

    </Grid>
  );
};

export default AddressFormSection;
